import { Box, Grid, Typography, useTheme } from '@mui/material';
import React from 'react';

import BarChart from 'components/charts/bar-chart';
import ChartCard from 'components/charts/chart-card';
import GaugeChart from 'components/charts/gauge-chart';
import LineChart from 'components/charts/line-chart';
import PieChart from 'components/charts/pie-chart';
import SparklineChart from 'components/charts/sparkline-chart';
import { useChartLocale } from 'components/charts/hooks/use-chart-locale';

const WEEKS = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8'];
const SITES = ['Northaven', 'Skarsvik', 'Baie Verte', 'Fjordholm'];

const BIOMASS_SERIES = [
  { id: 'northaven', label: 'Northaven biomass (kg)', data: [182000, 189500, 197200, 205800, 214100, 223900, 231400, 240800] },
  { id: 'skarsvik', label: 'Skarsvik biomass (kg)', data: [151000, 155800, 162300, 166900, 174500, 179200, 186700, 192300] },
];

const FEED_SERIES = [
  { id: 'pellet-3mm', label: '3 mm pellet (kg)', data: [14200, 11800, 16400, 9100] },
  { id: 'pellet-6mm', label: '6 mm pellet (kg)', data: [22600, 19400, 24800, 15300] },
  { id: 'pellet-9mm', label: '9 mm pellet (kg)', data: [8700, 12100, 10300, 6400] },
];

const SPECIES_SHARE = [
  { id: 'atlantic-salmon', label: 'Atlantic salmon', value: 62 },
  { id: 'rainbow-trout', label: 'Rainbow trout', value: 18 },
  { id: 'arctic-char', label: 'Arctic char', value: 9 },
  { id: 'cleaner-fish', label: 'Cleaner fish', value: 7 },
  { id: 'other', label: 'Other', value: 4 },
];

const SITE_TRENDS = [
  { id: 'northaven', label: 'Northaven', value: 240800, data: [182, 190, 197, 206, 214, 224, 231, 241] },
  { id: 'skarsvik', label: 'Skarsvik', value: 192300, data: [151, 156, 162, 167, 175, 179, 187, 192] },
  { id: 'baie-verte', label: 'Baie Verte', value: 168900, data: [175, 173, 171, 172, 170, 169, 170, 169] },
];

const CHART_PANEL_BODY_HEIGHT = 280;

const ChartPanel = ({ title, children, dark }: { title: string; children: React.ReactNode; dark: boolean }) => (
  <ChartCard title={title} dark={dark} height={CHART_PANEL_BODY_HEIGHT}>
    {children}
  </ChartCard>
);

// Light charts sit on white Paper; dark charts use the shared dark chart surface.
const ChartGrid = ({ dark }: { dark: boolean }) => {
  const theme = useTheme();
  const { formatValue } = useChartLocale();

  return (
    <Grid container spacing={4} alignItems="stretch">
      <Grid size={{ xs: 12, md: 6 }}>
        <ChartPanel title="Biomass by week" dark={dark}>
          <LineChart
            dark={dark}
            height={CHART_PANEL_BODY_HEIGHT}
            xAxis={[{ data: WEEKS, scaleType: 'point' }]}
            series={BIOMASS_SERIES}
            slotProps={{
              legend: {
                position: {
                  vertical: 'bottom',
                  horizontal: 'center',
                },
              },
            }}
          />
        </ChartPanel>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ChartPanel title="Feed by pellet size" dark={dark}>
          <BarChart
            dark={dark}
            height={CHART_PANEL_BODY_HEIGHT}
            xAxis={[{ data: SITES }]}
            series={FEED_SERIES}
            slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'center' } } }}
          />
        </ChartPanel>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <ChartPanel title="Species share" dark={dark}>
          <PieChart
            dark={dark}
            height={240}
            series={[{ data: SPECIES_SHARE, innerRadius: '55%', valueFormatter: ({ value }) => `${value}%` }]}
          />
        </ChartPanel>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <ChartPanel title="Pen utilization" dark={dark}>
          <GaugeChart dark={dark} height={240} value={72} text={({ value }) => `${value}%`} />
        </ChartPanel>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <ChartPanel title="Biomass trend" dark={dark}>
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            minHeight={CHART_PANEL_BODY_HEIGHT}
            justifyContent="space-between"
          >
            {SITE_TRENDS.map((site) => (
              <Box key={site.id} display="flex" alignItems="center" gap={3}>
                <Typography variant="body2" width={96} color={dark ? theme.palette.common.white : undefined}>
                  {site.label}
                </Typography>
                <Box flex="1 1 0%" minWidth={0}>
                  <SparklineChart dark={dark} data={site.data} height={36} disableKeyboardNavigation />
                </Box>
                <Typography variant="body2" color={dark ? theme.palette.common.white : undefined}>
                  {formatValue(site.value)} kg
                </Typography>
              </Box>
            ))}
          </Box>
        </ChartPanel>
      </Grid>
    </Grid>
  );
};

const PlaygroundCharts = () => {
  const theme = useTheme();

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="h2" color={theme.palette.common.white}>
          Charts
        </Typography>
        <Typography variant="body1" color={theme.palette.common.white}>
          Shared MUI X chart components rendered outside Data Lab, using only native props and the shared chart theme.
        </Typography>
      </Box>
      <ChartGrid dark={false} />
      <ChartGrid dark />
    </Box>
  );
};

export default PlaygroundCharts;
